// Фабрика персист-сторов меты (Ф2 §4.3): единая обвязка localStorage, которая
// раньше была скопирована в prefs/story/save/anon. Контракт:
//  - memory-кэш — единственный источник после первого get(); сторадж трогаем
//    best-effort в try/catch (private mode / битый json ничего не роняют);
//  - sanitize отвечает и за десериализацию сырья, и за дефолт: получает
//    undefined, когда записи нет или её не удалось прочитать;
//  - set() принимает УЖЕ валидный T — валидация патчей остаётся на сторе
//    (sanitize набора полей ≠ sanitize сырого JSON, см. storyStore: Set в
//    памяти против { watched: [] } в сторадже);
//  - storage-событие другой вкладки инвалидирует кэш и будит подписчиков
//    (в своей вкладке storage не стреляет — там уведомляет сам set()).

export interface PersistedStoreOptions<T> {
  /** значение → строка стораджа; дефолт JSON.stringify */
  encode?: (value: T) => string;
  /** строка стораджа → сырьё для sanitize; дефолт JSON.parse */
  decode?: (stored: string) => unknown;
}

export interface PersistedStore<T> {
  /** ленивое чтение: первый вызов поднимает из localStorage, дальше — кэш */
  get(): T;
  /** записать значение: кэш → сторадж (best-effort) → подписчики */
  set(next: T): T;
  /** полный сброс: кэш и ключ в сторадже; подписчики будятся */
  reset(): void;
  /** сбросить только кэш (ключ в сторадже цел, подписчики молчат) — следующий
   *  get() перечитает localStorage; нужен сторам с контрактом «читать всегда
   *  свежее» (anon: тесты и соседние вкладки пишут ключ напрямую) */
  invalidate(): void;
  subscribe(listener: () => void): () => void;
  /** счётчик правок — снапшот для useSyncExternalStore, когда T мутабелен (Set) */
  version(): number;
}

export function createPersistedStore<T>(
  key: string,
  sanitize: (raw: unknown) => T,
  opts: PersistedStoreOptions<T> = {},
): PersistedStore<T> {
  const encode = opts.encode ?? ((v: T) => JSON.stringify(v));
  const decode = opts.decode ?? ((s: string) => JSON.parse(s) as unknown);

  // отдельный флаг, а не сентинел null/undefined: T может быть любым
  let memory: T | undefined;
  let loaded = false;
  let version = 0;
  const subs = new Set<() => void>();

  const notify = (): void => {
    version += 1;
    subs.forEach((f) => f());
  };

  const load = (): T => {
    if (loaded) return memory as T;
    let raw: unknown;
    try {
      const stored = localStorage.getItem(key);
      raw = stored === null ? undefined : decode(stored);
    } catch {
      raw = undefined; // private mode / битый json → дефолт от sanitize
    }
    memory = sanitize(raw);
    loaded = true;
    return memory;
  };

  const invalidate = (): void => {
    loaded = false;
    memory = undefined;
  };

  // Другие вкладки: инвалидируем кэш — подписчики перечитают следующим get().
  // key === null — это clear(), задевает каждый стор.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      if (e.key !== null && e.key !== key) return;
      invalidate();
      notify();
    });
  }

  return {
    get: load,
    set(next: T): T {
      memory = next;
      loaded = true;
      try { localStorage.setItem(key, encode(next)); } catch { /* in-memory only */ }
      notify();
      return next;
    },
    reset(): void {
      invalidate();
      try { localStorage.removeItem(key); } catch { /* ignore */ }
      notify();
    },
    invalidate,
    subscribe(listener: () => void): () => void {
      subs.add(listener);
      return () => { subs.delete(listener); };
    },
    version: () => version,
  };
}
